import { useEffect, useState } from "react";
import { ExternalLink, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Loading, PageHeader } from "../../components/shared";

const GROUPS = [
  ["management", "Management", "/admin"],
  ["sales_crm", "Sales / CRM", "/admin/crm"],
  ["commercial", "Commercial", "/admin/commercial"],
  ["ecosystem", "Ecosystem", "/admin/societies"],
  ["operations", "Service / Project", "/admin/requirements"],
  ["finance_links", "Finance", "/admin/company-finance"],
];

export default function AdminReports() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [period, setPeriod] = useState("this_month");
  const canExport = user?.role === "super_admin" || user?.permissions?.management_reports?.export;

  const load = async () => {
    try {
      const response = await api.get("/admin/management/reports/catalog");
      setCatalog(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load reports");
      setCatalog({});
    }
  };

  useEffect(() => { load(); }, []);

  const download = async () => {
    try {
      const response = await api.get("/admin/management/reports/executive.csv", {
        params: { period },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "text/csv" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "management-executive-summary.csv";
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not export the report");
    }
  };

  if (!catalog) return <Loading />;

  return <div data-testid="management-reports-page"><PageHeader title="Report Centre" subtitle="A single navigation layer over existing management, CRM, commercial, operational, and finance reports."><Button data-testid="management-reports-refresh" onClick={load} variant="outline"><RefreshCw className="h-4 w-4" />Refresh</Button></PageHeader><section className="mb-8 border-l-4 border-primary bg-primary/5 p-5"><div className="flex flex-wrap items-end justify-between gap-4"><div><h2 className="font-display font-bold">Executive summary export</h2><p className="mt-1 text-sm text-muted-foreground">CSV export uses the same authoritative Company Finance figures as the Management Dashboard.</p></div>{canExport && <div className="flex gap-2"><select data-testid="management-report-period" onChange={(event) => setPeriod(event.target.value)} value={period}><option value="this_month">This Month</option><option value="last_month">Last Month</option><option value="this_quarter">This Quarter</option><option value="this_financial_year">This Financial Year</option><option value="last_financial_year">Last Financial Year</option></select><Button data-testid="management-executive-export" onClick={download}><FileDown className="h-4 w-4" />Export CSV</Button></div>}</div></section><section className="grid gap-5 lg:grid-cols-2">{GROUPS.map(([key, title, route]) => <article className="border bg-card p-5" data-testid={`management-report-group-${key}`} key={key}><div className="flex items-center justify-between gap-3"><h2 className="font-display font-bold">{title}</h2><a data-testid={`management-report-link-${key}`} href={route}><Button size="icon" variant="ghost"><ExternalLink className="h-4 w-4" /></Button></a></div><ul className="mt-4 space-y-2 text-sm text-muted-foreground">{(catalog[key] || []).map((report) => <li className="border-l-2 border-primary/30 pl-3" key={report}>{report}</li>)}</ul></article>)}</section></div>;
}